import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { InputObject } from "../../controls/default";
import { CounsellingRequestStatus } from "../../repository/data.default";
import { Tag, FormInstance } from "antd";
import { Dayjs } from "dayjs";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import {
  createSearchRequest,
  createScopedFilterRequest,
  createStaticOptionsRequest,
} from "../../hooks/useSearchRequest";
import {
  getCompanies,
  getCompanyDepartments,
  getPlatformUsers,
  getCollaborators,
} from "../../repository/data.repository";
import { createStatusColorMap } from "../../utils/colors";

export interface CounsellingRequestsViewForm {
  readonly company: InputObject;
  readonly department: InputObject;
  readonly patient: InputObject;
  readonly therapist: InputObject;
  readonly status: InputObject<CounsellingRequestStatus>;
  readonly createdOn: Dayjs[];
  readonly createdByUserUserName: string;
  readonly updatedOn: Dayjs[];
  readonly updatedByUserUserName: string;
  readonly scheduledAt: Dayjs[];
}

// Status → tag colour, shared by the grid column and the manage dialog.
export const counsellingStatusColor = createStatusColorMap<string>({
  [CounsellingRequestStatus.Requested]: "default",
  [CounsellingRequestStatus.Proposed]: "gold",
  [CounsellingRequestStatus.Scheduled]: "blue",
  [CounsellingRequestStatus.Completed]: "green",
  [CounsellingRequestStatus.Cancelled]: "red",
});

export const counsellingRequestsViewColumns: DataColumn[] = [
  {
    title: "Company",
    key: "companyName",
    width: 180,
    sort: true,
    defaultSort: "asc",
  },
  {
    title: "Patient Name",
    key: "patientUserName",
    width: 180,
    sort: true,
  },
  {
    title: "Therapist Name",
    key: "therapistUserName",
    width: 180,
    sort: true,
  },
  {
    title: "Status",
    key: "status",
    width: 140,
    sort: true,
    transform: (value: string) => (
      <Tag color={counsellingStatusColor(value)}>{value}</Tag>
    ),
  },
  {
    title: "Reason",
    key: "reason",
    width: 220,
    sort: false,
  },
  {
    title: "Proposed At",
    key: "proposedAt",
    width: 170,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Scheduled At",
    key: "scheduledAt",
    width: 170,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Requested By",
    key: "requestedByUserName",
    width: 180,
    sort: true,
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 170,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Updated On",
    key: "updatedOn",
    width: 170,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
];

const counsellingRequestsQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "patient", queryField: "patientUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "therapist", queryField: "therapistUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "status", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "createdByUserUserName", operation: QueryOperation.Contains },
  { formField: "updatedOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "updatedByUserUserName", operation: QueryOperation.Contains },
  { formField: "scheduledAt", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapCounsellingRequestsViewQuery = (values: CounsellingRequestsViewForm): SearchQuery =>
  buildSearchQuery(values, counsellingRequestsQueryMappings);

// Factory: Department scopes to the chosen Company, Patient to the chosen Company + Department (soft scope;
// `dependsOn` clears stale children). Department is scoping-only — it narrows the Patient picker but is not
// itself a query condition (counselling filters by patientUid / companyUid).
export const counsellingRequestsSearchSchema = (
  form: FormInstance<any>,
): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "department",
          label: "Department",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ]),
        },
        {
          type: "combobox",
          name: "patient",
          label: "Patient",
          dependsOn: ["company", "department"],
          request: createScopedFilterRequest(getPlatformUsers, "userName", () => [
            ["companyUid", form.getFieldValue("company")?.value],
            ["departmentUid", form.getFieldValue("department")?.value],
          ]),
        },
        {
          type: "combobox",
          name: "therapist",
          label: "Therapist",
          request: createSearchRequest(getCollaborators, "userName"),
        },
        {
          type: "combobox",
          name: "status",
          label: "Status",
          request: createStaticOptionsRequest(
            Object.values(CounsellingRequestStatus).map((s) => ({ display: s, value: s })),
          ),
        },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
        { type: "textbox", name: "createdByUserUserName", label: "Created By" },
        { type: "rangetimepicker", name: "updatedOn", label: "Updated On" },
        { type: "textbox", name: "updatedByUserUserName", label: "Updated By" },
        { type: "rangetimepicker", name: "scheduledAt", label: "Scheduled At" },
      ],
    },
  ],
});
